import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';

// @ts-ignore
import Player from '@vimeo/player';
import { HHDSVideoEvent } from '../../utils/VideoTypes';

const DEBUG_VERBOSE: boolean = false;
export const HHDSVideoVimeoTagName: string = 'hhds-video-vimeo';
const TAG_NAME: string = HHDSVideoVimeoTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSVideoVimeoAttrNames = {
	src: 'src',
	poster: 'poster',
	autoplay: 'autoplay',
	loop: 'loop',
	controls: 'controls',
	useContainer: 'use-container',
};

export class HHDSVideoVimeo extends Component {
	private _posterElement: HTMLElement | null = null;
	private _player: Player | null = null;

	constructor() {
		super();
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		let html: string = '';

		if (this.vars.get<boolean>(HHDSVideoVimeoAttrNames.useContainer)) {
			html = `
      <div class="${TAG_NAME}">
				<div class="${TAG_NAME}__container container container--fullwidth">
					<div class="grid ${TAG_NAME}__grid">
						<div class="${TAG_NAME}__wrapper col-span-4 md:col-start-2 md:col-span-10 lg:col-start-3 lg:col-span-20">
							<div class="${TAG_NAME}__video-container"></div>
							${this.coverImageHtml}
						</div>
					</div>
				</div>
			</div>
    `;
		} else {
			html = `
      <div class="${TAG_NAME}">
				<div class="${TAG_NAME}__wrapper col-span-4 md:col-start-2 md:col-span-10 lg:col-start-3 lg:col-span-20">
					<div class="${TAG_NAME}__video-container"></div>
					${this.coverImageHtml}
				</div>
			</div>
    `;
		}

		this.setInnerHtml('', '', html);

		this.observeSlotChanges(true);

		const focusTarget = this.element(`.${TAG_NAME}__poster`);
		if (focusTarget) {
			focusTarget.setAttribute('role', 'button');
			focusTarget.setAttribute('tabindex', '0');
			focusTarget.setAttribute('aria-label', 'Play video');
			focusTarget.addEventListener('keydown', (event: KeyboardEvent) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					this._posterElement?.click();
				}
			});
		}

		this._posterElement = this.querySelector(`.${TAG_NAME}__poster`) as HTMLElement;
		if (this._posterElement) {
			const coverImageUrlString = this.vars.get<string>(HHDSVideoVimeoAttrNames.poster);
			if (coverImageUrlString && coverImageUrlString.length > 0) {
				const image = new Image();
				image.addEventListener('load', () => {
					if (this._posterElement) {
						this._posterElement.style.backgroundImage = 'url(' + coverImageUrlString + ')';
					}
					this.emitEvent(HHDSVideoEvent.coverLoaded);
				});
				image.src = coverImageUrlString;
			}
		}

		if (this._posterElement) {
			this.initVimeoPlayer();
		}

		const updateForBreakpoint = () => {
			const useGrid: boolean = this.currentBreakpoint == 'lg' || this.currentBreakpoint == 'xl';
			this.element(`.${TAG_NAME}__grid`)?.classList.toggle('grid', useGrid);
		};
		this.observeBreakpointChanges((_breakpoint: string) => updateForBreakpoint());
		updateForBreakpoint();
	}

	get coverImageHtml(): string {
		if (this.vars.get<string>(HHDSVideoVimeoAttrNames.poster) === '') {
			return ``;
		} else {
			return this.vars.get<boolean>(HHDSVideoVimeoAttrNames.autoplay)
				? `<div class="${TAG_NAME}__poster"></div>`
				: `<div class="${TAG_NAME}__poster">
					<svg class="play-icon" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
						<rect class="bg" width="80" height="80" rx="40" />
						<path class="triangle" d="M54 40.0006C54.0008 40.3402 53.9138 40.6741 53.7473 40.9701C53.5808 41.266 53.3406 41.5138 53.05 41.6894L35.04 52.7069C34.7364 52.8928 34.3886 52.9943 34.0326 53.0009C33.6766 53.0075 33.3253 52.9189 33.015 52.7444C32.7076 52.5725 32.4516 52.3219 32.2732 52.0183C32.0948 51.7147 32.0005 51.369 32 51.0169V28.9844C32.0005 28.6322 32.0948 28.2866 32.2732 27.983C32.4516 27.6793 32.7076 27.4287 33.015 27.2569C33.3253 27.0823 33.6766 26.9938 34.0326 27.0003C34.3886 27.0069 34.7364 27.1084 35.04 27.2944L53.05 38.3119C53.3406 38.4875 53.5808 38.7353 53.7473 39.0312C53.9138 39.3271 54.0008 39.6611 54 40.0006Z"/>
					</svg>
				</div>`;
		}
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		this._player?.destroy();
		this._player = null;
	}

	posterImage(): string {
		if (this.vars.get<string>(HHDSVideoVimeoAttrNames.poster) === '') {
			return ``;
		} else {
			return this.vars.get<boolean>(HHDSVideoVimeoAttrNames.autoplay)
				? `<div class="${TAG_NAME}__poster ${TAG_NAME}__autoplay" style="background-image: url(${this.vars.get<string>(HHDSVideoVimeoAttrNames.poster)})"></div>`
				: `<div class="${TAG_NAME}__poster" style="background-image: url(${this.vars.get<string>(HHDSVideoVimeoAttrNames.poster)})"></div>`;
		}
	}

	initVimeoPlayer(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'initVimeoPlayer');

		const ap = this.vars.get<boolean>(HHDSVideoVimeoAttrNames.autoplay);
		const controls = this.vars.get<boolean>(HHDSVideoVimeoAttrNames.controls);
		const bg = this.vars.get<boolean>(HHDSVideoVimeoAttrNames.autoplay) && !this.vars.get<boolean>(HHDSVideoVimeoAttrNames.controls);
		const loop = this.vars.get<boolean>(HHDSVideoVimeoAttrNames.loop);

		const container = this.element(`.${TAG_NAME}__video-container`);
		const src = this.vars.get<string>(HHDSVideoVimeoAttrNames.src);
		this._player = new Player(container, {
			url: src,
			autoplay: ap,
			background: bg,
			muted: ap,
			controls: controls,
			loop: loop,
		});

		this._player.ready().then(() => {
			this.toggleCover(true);
			this.emitEvent(HHDSVideoEvent.loaded);
			this._posterElement?.addEventListener('click', () => {
				// this._player.setMuted(true);
				this.toggleCover(false);
				this.emitEvent(HHDSVideoEvent.playRequest);
				this._player.play();
			});

			this._player.on('play', () => {
				DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'Vimeo video is playing');
				this.toggleCover(false);
				this.emitEvent(HHDSVideoEvent.play);
			});
			this._player.on('pause', () => {
				DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'Vimeo video is paused');
				this.emitEvent(HHDSVideoEvent.pause);
			});
			this._player.on('ended', () => {
				DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'Vimeo video has ended');
				this.toggleCover(true);
				this._player.element.src = this._player.element.src;
				this.emitEvent(HHDSVideoEvent.ended);
			});
		});
	}

	pause(): void {
		this._player?.pause();
	}

	private toggleCover(visible: boolean) {
		DEBUG_VERBOSE && console.log('[HHDSVideoVimeo] Toggling cover: ' + this.id, 'visible=', visible);
		if (this._posterElement) this._posterElement.style.display = visible ? 'flex' : 'none';

		const container = this.element(`.${TAG_NAME}__video-container`);
		if (container) {
			container.style.pointerEvents = visible ? 'none' : 'auto';
			container.setAttribute('aria-hidden', visible ? 'true' : 'false');
			const playerElement = container.querySelector('iframe');
			if (playerElement) playerElement.style.display = visible ? 'none' : 'block';
		}
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[HHDSVideoVimeoAttrNames.src]: {
		description: 'URL of the video in the form https://player.vimeo.com/video/1062870380',
		defaultValue: '',
		type: String,
	},
	[HHDSVideoVimeoAttrNames.poster]: {
		description: 'URL of the poster image',
		type: String,
		defaultValue: '',
	},
	[HHDSVideoVimeoAttrNames.autoplay]: {
		description: 'Autoplay the video when it is ready. Note that this will also muted the video',
		type: Boolean,
		defaultValue: false,
	},
	[HHDSVideoVimeoAttrNames.loop]: {
		description: 'Loop the video',
		type: Boolean,
		defaultValue: false,
	},
	[HHDSVideoVimeoAttrNames.controls]: {
		description: 'Show the player controls',
		type: Boolean,
		defaultValue: true,
	},
	[HHDSVideoVimeoAttrNames.useContainer]: {
		description: 'Whether to use a CSS container for the video player',
		defaultValue: true,
		type: Boolean,
	},
};
